import type { InventoryItem } from '../types'

export interface ProductColor {
  id: string
  name: string
  hex_code?: string
}

export interface ProductSize {
  id: string
  name: string
  sort_order: number
}

export interface ProductColorGroup {
  colorId: string
  colorName: string
  hexCode?: string
  totalQty: number
  sizeQuantities: Record<string, number>
  sizeItemIds: Record<string, string>
  sizeSellPrices: Record<string, number>
}

export interface ProductNameGroup {
  productName: string
  category?: string
  imageUrl?: string
  sellPrice?: number
  referenceCode?: string
  variantCount: number
  totalQuantity: number
  colors: ProductColorGroup[]
  variants: InventoryItem[]
}

function getColorInfo(
  colorId: string | undefined,
  colors: ProductColor[]
): { name: string; hexCode?: string } {
  if (!colorId) return { name: '-' }
  const color = colors.find(c => c.id === colorId)
  return { name: color?.name || '-', hexCode: color?.hex_code }
}

function getSizeName(sizeId: string | undefined, sizes: ProductSize[]): string | undefined {
  if (!sizeId) return undefined
  return sizes.find(s => s.id === sizeId)?.name
}

export function buildProductNameGroups(
  items: InventoryItem[],
  colors: ProductColor[],
  sizes: ProductSize[],
  getQuantity: (productId: string) => number
): ProductNameGroup[] {
  const groups: Record<string, ProductNameGroup> = {}

  for (const item of items) {
    const groupKey = item.name || 'Unnamed Product'
    const itemQuantity = getQuantity(item.id)

    if (!groups[groupKey]) {
      groups[groupKey] = {
        productName: groupKey,
        category: item.category,
        imageUrl: item.image_url,
        sellPrice: item.sell_price ?? item.unit_price,
        referenceCode: item.reference_code,
        variantCount: 0,
        totalQuantity: 0,
        colors: [],
        variants: []
      }
    }

    const group = groups[groupKey]
    group.variants.push(item)
    group.variantCount += 1
    group.totalQuantity += itemQuantity

    if (item.image_url && !group.imageUrl) group.imageUrl = item.image_url
    if (item.reference_code && !group.referenceCode) group.referenceCode = item.reference_code
    if ((item.sell_price ?? item.unit_price) && !group.sellPrice) {
      group.sellPrice = item.sell_price ?? item.unit_price
    }
    if (item.category && !group.category) group.category = item.category

    const colorInfo = getColorInfo(item.color_id, colors)
    const colorKey = item.color_id || 'no-color'
    const sizeName = getSizeName(item.size_id, sizes) || 'No Size'

    let colorVariant = group.colors.find(c => c.colorId === colorKey)
    if (!colorVariant) {
      colorVariant = {
        colorId: colorKey,
        colorName: colorInfo.name,
        hexCode: colorInfo.hexCode,
        totalQty: 0,
        sizeQuantities: {},
        sizeItemIds: {},
        sizeSellPrices: {}
      }
      group.colors.push(colorVariant)
    }

    colorVariant.totalQty += itemQuantity
    colorVariant.sizeQuantities[sizeName] =
      (colorVariant.sizeQuantities[sizeName] || 0) + itemQuantity
    if (!colorVariant.sizeItemIds[sizeName]) {
      colorVariant.sizeItemIds[sizeName] = item.id
      colorVariant.sizeSellPrices[sizeName] = item.sell_price ?? item.unit_price ?? 0
    }
  }

  for (const group of Object.values(groups)) {
    group.colors.sort((a, b) => a.colorName.localeCompare(b.colorName))
  }

  return Object.values(groups).sort((a, b) => a.productName.localeCompare(b.productName))
}

export function filterProductGroups(
  groups: ProductNameGroup[],
  searchQuery: string,
  colors: ProductColor[]
): ProductNameGroup[] {
  if (!searchQuery.trim()) return groups

  const searchLower = searchQuery.toLowerCase().trim()

  return groups.filter(group => {
    if (group.productName.toLowerCase().includes(searchLower)) return true
    if (group.referenceCode?.toLowerCase().includes(searchLower)) return true
    if (group.category?.toLowerCase().includes(searchLower)) return true
    if (group.colors.some(c => c.colorName.toLowerCase().includes(searchLower))) return true
    return group.variants.some(v =>
      v.sku?.toLowerCase().includes(searchLower) ||
      v.barcode?.toLowerCase().includes(searchLower) ||
      v.reference_code?.toLowerCase().includes(searchLower)
    )
  })
}

export function getSortedSizeColumns(
  groups: ProductNameGroup[],
  sizes: ProductSize[]
): string[] {
  const sizeSet = new Set<string>()
  for (const group of groups) {
    for (const color of group.colors) {
      Object.keys(color.sizeQuantities).forEach(s => sizeSet.add(s))
    }
  }

  const sorted = sizes
    .map(s => s.name)
    .filter(name => sizeSet.has(name))

  if (sizeSet.has('No Size') && !sorted.includes('No Size')) {
    sorted.push('No Size')
  }

  for (const name of sizeSet) {
    if (!sorted.includes(name)) sorted.push(name)
  }

  return sorted
}
